import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api', // For auth and other backend routes
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/signin';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  // Sign up
  signUp: async (userData) => {
    const response = await api.post('/auth/signup', userData);
    return response.data;
  },

  // Sign in
  signIn: async (credentials) => {
    const response = await api.post('/auth/signin', credentials);
    return response.data;
  },

  // Get current user profile
  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Chat API calls
export const chatAPI = {
  sendMessage: async (message) => {
    try {
      // Use the backend chat API which handles individual user webhook URLs
      const response = await api.post('/chat', { text: message });
      
      return {
        success: true,
        response: response.data.reply,
        data: response.data
      };
    } catch (error) {
      console.error('Chat API error:', error);
      throw error;
    }
  },

  // Voice-specific API calls
  speechToText: async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob);
      
      const response = await api.post('/voice/speech-to-text', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Speech to text error:', error);
      throw error;
    }
  },

  textToSpeech: async (text) => {
    try {
      // Use backend TTS with per-user settings
      const response = await api.post('/voice/text-to-speech', { text });
      return response.data.audio;
    } catch (error) {
      console.error('Text to speech error:', error);
      throw error;
    }
  },

  // Settings API calls
  getSettings: async () => {
    try {
      const response = await api.get('/v2/admin/me/settings');
      return response.data.settings;
    } catch (error) {
      console.error('Get settings error:', error);
      throw error;
    }
  },

  updateSettings: async (settings) => {
    try {
      const response = await api.put('/v2/admin/me/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Update settings error:', error);
      throw error;
    }
  }
};

export default api;
